import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { formatDate } from "../utils/dateUtils";

function Notifications() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [lessons, setLessons] = useState([]); // Store lessons to check candidateId
  const [candidates, setCandidates] = useState([]); // Store candidates to check instructor assignment
  const [userRole, setUserRole] = useState(null);
  const [userEntityId, setUserEntityId] = useState(null); // Candidate or instructor document ID
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterEntityType, setFilterEntityType] = useState("");
  const [filterRead, setFilterRead] = useState("");
  const itemsPerPage = 30;

  // Fetch user role and entityId
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || null);
            setUserEntityId(userData.entityId || null);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUserRole(null);
        setUserEntityId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch lessons (needed to check candidateId for lesson notifications)
  const fetchLessons = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "lessons"));
      const lessonsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLessons(lessonsData);
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  }, []);

  // Fetch candidates (needed to check instructor assignment for instructor notifications)
  const fetchCandidates = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "candidates"));
      const candidatesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching candidates:", error);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const q = query(
        collection(db, "notifications"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const notificationsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotifications(notificationsData);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchLessons();
    fetchCandidates();
  }, [fetchNotifications, fetchLessons, fetchCandidates]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, "notifications", notificationId);
      await updateDoc(notificationRef, { read: true });
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark all as read - only mark notifications the user can actually update
  const markAllAsRead = async () => {
    try {
      // Filter to only include notifications the user can update
      const unreadNotifications = notifications.filter((n) => {
        if (n.read) return false;

        // For candidates, only mark notifications they own
        if (userRole === "candidate" && userEntityId) {
          return (
            n.candidateId === userEntityId ||
            n.recipientId === auth.currentUser?.uid ||
            n.entityType === "lecture" // All candidates can mark lecture notifications as read
          );
        }

        // For instructors, only mark notifications related to their candidates or themselves
        if (userRole === "instructor" && userEntityId) {
          // Check if notification is related to a candidate assigned to this instructor
          if (n.candidateId) {
            const candidate = candidates.find((c) => c.id === n.candidateId);
            if (candidate && candidate.assignedInstructorId === userEntityId) {
              return true;
            }
          }
          // Check if notification is related to this instructor
          if (n.entityType === "instructor" && n.entityId === userEntityId) {
            return true;
          }
          // Check if notification has recipientId matching their authUid
          if (n.recipientId === auth.currentUser?.uid) {
            return true;
          }
          // All instructors can mark lecture notifications as read
          if (n.entityType === "lecture") {
            return true;
          }
          return false;
        }

        // For admins, mark all
        return true;
      });

      const updatePromises = unreadNotifications.map((notif) =>
        updateDoc(doc(db, "notifications", notif.id), { read: true })
      );
      await Promise.all(updatePromises);
      setNotifications((prev) =>
        prev.map((notif) => {
          // Only mark as read if it was in the list of updatable notifications
          const shouldMark = unreadNotifications.some((n) => n.id === notif.id);
          return shouldMark ? { ...notif, read: true } : notif;
        })
      );
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    if (type.includes("expiring") || type.includes("_due")) {
      return (
        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
      );
    } else if (type.includes("added")) {
      return (
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </div>
      );
    } else if (type.includes("updated") || type.includes("edited")) {
      return (
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>
      );
    } else if (type.includes("deleted")) {
      return (
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        <svg
          className="w-5 h-5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    );
  };

  // Get notification message
  const getNotificationMessage = (notification) => {
    const entityName = notification.entityName || "";
    const entityType = notification.entityType || "";
    const expiryDate = notification.expiryDate
      ? formatDate(notification.expiryDate)
      : "";
    const dueDate = notification.dueDate
      ? formatDate(notification.dueDate)
      : "";
    const daysUntilExpiry = notification.daysUntilExpiry ?? null;
    const daysUntilDue = notification.daysUntilDue ?? null;
    const daysValue = daysUntilExpiry ?? daysUntilDue ?? null;

    // Get days text (singular/plural)
    let daysText = "days";
    if (daysValue !== null) {
      if (daysValue === 0) {
        daysText = t("notifications.daysText.today");
      } else if (daysValue === 1) {
        daysText = t("notifications.daysText.day");
      } else {
        daysText = t("notifications.daysText.days");
      }
    }

    const key = `notifications.messages.${notification.type}`;
    const defaultMessage = t(key, {
      entityName,
      entityType,
      expiryDate,
      dueDate,
      daysUntilExpiry: daysUntilExpiry ?? 0,
      daysUntilDue: daysUntilDue ?? 0,
      daysText,
    });

    // If translation doesn't exist, return a fallback
    if (defaultMessage === key) {
      return `${t(
        `notifications.actions.${notification.type}`
      )}: ${entityName}`;
    }

    return defaultMessage;
  };

  // Filter notifications based on user role
  const filteredNotifications = useMemo(() => {
    let filtered = notifications;

    // For candidates, only show notifications related to them
    if (userRole === "candidate" && userEntityId) {
      filtered = notifications.filter((notification) => {
        // Show notifications where candidateId matches their candidate document ID
        if (notification.candidateId === userEntityId) {
          return true;
        }

        // Show notifications where entityType is "candidate" and entityId matches their candidate document ID
        if (
          notification.entityType === "candidate" &&
          notification.entityId === userEntityId
        ) {
          return true;
        }

        // Show notifications where entityType is "lesson" and the lesson's candidateId matches their candidate document ID
        if (notification.entityType === "lesson") {
          const lesson = lessons.find((l) => l.id === notification.entityId);
          if (lesson && lesson.candidateId === userEntityId) {
            return true;
          }
          // Also check if notification has candidateId field
          if (notification.candidateId === userEntityId) {
            return true;
          }
        }

        // Show notifications where entityType is "lecture" (all lectures are relevant to candidates)
        if (notification.entityType === "lecture") {
          return true;
        }

        // Show notifications with recipientId matching their authUid
        if (notification.recipientId === auth.currentUser?.uid) {
          return true;
        }

        return false;
      });
    }

    // For instructors, only show notifications related to their candidates or themselves
    if (userRole === "instructor" && userEntityId) {
      filtered = notifications.filter((notification) => {
        // Show notifications where the lesson's candidate is assigned to this instructor
        if (notification.entityType === "lesson") {
          const lesson = lessons.find((l) => l.id === notification.entityId);
          if (lesson) {
            // Check if the candidate from this lesson is assigned to this instructor
            const candidate = candidates.find(
              (c) => c.id === lesson.candidateId
            );
            if (candidate && candidate.assignedInstructorId === userEntityId) {
              return true;
            }
          }
          // Also check if notification has candidateId and that candidate is assigned to this instructor
          if (notification.candidateId) {
            const candidate = candidates.find(
              (c) => c.id === notification.candidateId
            );
            if (candidate && candidate.assignedInstructorId === userEntityId) {
              return true;
            }
          }
        }

        // Show notifications related to candidates assigned to this instructor
        if (notification.candidateId) {
          const candidate = candidates.find(
            (c) => c.id === notification.candidateId
          );
          if (candidate && candidate.assignedInstructorId === userEntityId) {
            return true;
          }
        }

        // Show notifications related to instructor document expiry (for this instructor)
        if (
          notification.entityType === "instructor" &&
          notification.entityId === userEntityId
        ) {
          return true;
        }

        // Show notifications with recipientId matching their authUid
        if (notification.recipientId === auth.currentUser?.uid) {
          return true;
        }

        // Show lecture notifications (all instructors can see lectures)
        if (notification.entityType === "lecture") {
          return true;
        }

        return false;
      });
    }

    // Apply other filters (search, type, entityType, read status)
    return filtered.filter((notification) => {
      const matchesSearch =
        !searchQuery ||
        notification.entityName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        getNotificationMessage(notification)
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesType = !filterType || notification.type === filterType;
      const matchesEntityType =
        !filterEntityType || notification.entityType === filterEntityType;
      const matchesRead =
        filterRead === "" ||
        (filterRead === "read" && notification.read) ||
        (filterRead === "unread" && !notification.read);

      return matchesSearch && matchesType && matchesEntityType && matchesRead;
    });
  }, [
    notifications,
    lessons,
    candidates,
    userRole,
    userEntityId,
    searchQuery,
    filterType,
    filterEntityType,
    filterRead,
    t,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNotifications = filteredNotifications.slice(
    startIndex,
    endIndex
  );

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType, filterEntityType, filterRead]);

  // Get unique types and entity types for filters - use filtered notifications
  const uniqueTypes = useMemo(() => {
    const types = new Set();
    filteredNotifications.forEach((n) => types.add(n.type));
    return Array.from(types).sort();
  }, [filteredNotifications]);

  const uniqueEntityTypes = useMemo(() => {
    const types = new Set();
    filteredNotifications.forEach((n) => types.add(n.entityType));
    return Array.from(types).sort();
  }, [filteredNotifications]);

  // Count unread notifications - only count notifications the user can actually see
  const unreadCount = useMemo(() => {
    return filteredNotifications.filter((n) => !n.read).length;
  }, [filteredNotifications]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("notifications.title")}
            </h2>
            <p className="text-gray-600">{t("notifications.subtitle")}</p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              {t("notifications.markAllAsRead")} ({unreadCount})
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">
              {t("notifications.stats.total")}
            </h3>
            <p className="text-3xl font-bold text-gray-900">
              {filteredNotifications.length}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">
              {t("notifications.stats.unread")}
            </h3>
            <p className="text-3xl font-bold text-red-600">{unreadCount}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">
              {t("notifications.stats.read")}
            </h3>
            <p className="text-3xl font-bold text-green-600">
              {filteredNotifications.length - unreadCount}
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Search Bar */}
            <div className="relative flex-1 col-span-full lg:col-span-2">
              <input
                type="text"
                placeholder={t("notifications.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-10 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("notifications.filters.allTypes")}</option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {t(`notifications.actions.${type}`)}
                </option>
              ))}
            </select>

            {/* Entity Type Filter */}
            <select
              value={filterEntityType}
              onChange={(e) => setFilterEntityType(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("notifications.filters.allEntities")}</option>
              {uniqueEntityTypes.map((type) => (
                <option key={type} value={type}>
                  {t(`notifications.entityTypes.${type}`)}
                </option>
              ))}
            </select>

            {/* Read Status Filter */}
            <select
              value={filterRead}
              onChange={(e) => setFilterRead(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("notifications.filters.allStatus")}</option>
              <option value="unread">
                {t("notifications.filters.unread")}
              </option>
              <option value="read">{t("notifications.filters.read")}</option>
            </select>

            {/* Clear Filters */}
            {(searchQuery || filterType || filterEntityType || filterRead) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setFilterType("");
                  setFilterEntityType("");
                  setFilterRead("");
                }}
                className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>{t("notifications.clearAll")}</span>
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        {!loading && filteredNotifications.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 px-2">
            <span>
              {t("notifications.showing")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredNotifications.length > 0
                  ? `${startIndex + 1}-${Math.min(
                      endIndex,
                      filteredNotifications.length
                    )}`
                  : 0}
              </span>{" "}
              {t("notifications.of")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredNotifications.length}
              </span>{" "}
              {t("notifications.notificationsText")}
            </span>
          </div>
        )}

        {/* Notifications List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <p>{t("notifications.noNotifications")}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {paginatedNotifications.map((notification) => {
                const createdAt = notification.createdAt?.toDate
                  ? notification.createdAt.toDate()
                  : new Date(notification.createdAt);

                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notification.read ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p
                              className={`text-sm ${
                                !notification.read
                                  ? "font-semibold text-gray-900"
                                  : "text-gray-700"
                              }`}
                            >
                              {getNotificationMessage(notification)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(createdAt)} •{" "}
                              {t(
                                `notifications.entityTypes.${notification.entityType}`
                              )}
                            </p>
                          </div>
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="ml-4 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              {t("notifications.markAsRead")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredNotifications.length > itemsPerPage && (
          <div className="flex items-center justify-center space-x-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.previous")}
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-black text-white border-black"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Notifications;
